import _ from 'lodash';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { OrderModalBody } from '../../components/order/order-modal-body';
import { pricePlaceHolder } from '../../components/order/price-select';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { Feature, useFeature } from '../../hooks/use-feature';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useOrderModal() {
  const storage = useStorage();
  const { spot, menuInfo } = storage;
  const { analyticsPageView, analyticsFeatureUseEvent } = useAnalytics();
  const { swal } = useSwal({ storage });
  const { handleErrorOnModal } = useErrorHandler();
  const { t } = useTranslation();
  const { isFeatureEnabled } = useFeature();
  const isSelfOrderingEnabled = isFeatureEnabled(Feature.SELF_ORDERING);

  const defaultOrderRef = useRef();
  const newOrderRef = useRef();
  const uniqueRef = useRef();

  const categoryRef = useRef();
  const itemRef = useRef();

  const didOpen = async () => {
    analyticsPageView('Ordering');
    uniqueRef.current = uuidv4();
    console.log(`unique: ${uniqueRef.current}`);
  };

  const onChange = async (order) => {
    swal?.resetValidationMessage();
    newOrderRef.current = order;
  };

  const onDefaultSet = (order) => {
    defaultOrderRef.current = order;
  };

  async function prepare({ category, item }) {
    categoryRef.current = category;
    itemRef.current = item;
  }

  async function show({ prepareOnPreConfirm } = {}) {
    const result = await swal.fire({
      html: (
        <OrderModalBody
          item={itemRef.current}
          category={categoryRef.current}
          analyticsFeatureUseEvent={analyticsFeatureUseEvent}
          onChange={onChange}
          onDefaultSet={onDefaultSet}
          symbol={menuInfo.i18n?.currency?.symbol}
          isSelfOrderingEnabled={isSelfOrderingEnabled}
        />
      ),
      cancelButtonText: t('back'),
      showCancelButton: true,
      didOpen,
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      showConfirmButton: isSelfOrderingEnabled,
      confirmButtonText: t(spot?.prepaid ? 'order.add-item' : 'order.make'),
      showCloseButton: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: true,
      showClass: {
        popup: 'animate__animated animate__slideInRight animate__faster',
      },
    });

    result.hasLostChanges =
      isSelfOrderingEnabled &&
      result.isDismissed &&
      !_.isEqual(defaultOrderRef.current, newOrderRef.current);

    result.newOrder = newOrderRef.current;
    return result;
  }

  const preConfirm = async ({ prepareOnPreConfirm }) => {
    const order = newOrderRef.current;

    let optionGroups = itemRef.current.option_groups;
    if (_.isEmpty(optionGroups))
      optionGroups = categoryRef.current.option_groups;

    for (let index = 0; index < optionGroups?.length; index += 1) {
      const optionGroup = optionGroups[index];

      const groupOptionsIds = optionGroup.options?.map((o) => o.id);
      const selectedOptions =
        order?.options?.filter((o) => groupOptionsIds.includes(o.id)) ?? [];

      if (optionGroup.required_min > selectedOptions.length) {
        if (optionGroup.required_min === 1) {
          swal.showValidationMessage(
            t('order.choose-group', {
              article: optionGroup.article,
              name: optionGroup.name,
            })
          );
          return null;
        }

        swal.showValidationMessage(
          t('order.choose.at-least', {
            name: optionGroup.name.toUpperCase(),
            min: optionGroup.required_min,
          })
        );
        return null;
      }

      if (optionGroup.required_max < selectedOptions.length) {
        swal.showValidationMessage(
          t('order.choose.max', {
            name: optionGroup.name.toUpperCase(),
            max: optionGroup.required_min,
          })
        );
        return null;
      }
    }

    if (order?.price == null) {
      swal.showValidationMessage(t(pricePlaceHolder));
      return null;
    }

    if (parseInt(order?.quantity ?? 0, 10) === 0) {
      swal.showValidationMessage(t('order.choose-quantity'));
      return null;
    }

    try {
      await prepareOnPreConfirm?.call(this, { newOrder: newOrderRef.current });
    } catch (error) {
      handleErrorOnModal(error);
      swal.hideLoading();
      return false;
    }

    return true;
  };

  return {
    showOrderModal: show,
    prepareOrderModal: prepare,
  };
}

export { useOrderModal };
